export type InstallPlatform = 'ios' | 'android' | null;

export interface PwaInstallState {
  platform: InstallPlatform;
  visible: boolean;
  canPrompt: boolean;
  installing: boolean;
  error: string | null;
}

export interface PwaInstallControls extends PwaInstallState {
  install: () => Promise<void>;
  dismiss: () => void;
}

interface InstallChoice {
  outcome: 'accepted' | 'dismissed';
}

interface InstallPromptEvent extends Event {
  prompt: () => Promise<InstallChoice | void>;
  userChoice?: Promise<InstallChoice>;
}

type InstallBrowser = Pick<Window, 'navigator' | 'matchMedia' | 'sessionStorage' | 'addEventListener' | 'removeEventListener'>;
const dismissalKey = 'pq-install-dismissed';
// Keep dismissal for this page session even when private browsing blocks storage.
const dismissedBrowsers = new WeakSet<InstallBrowser>();

export const getInstallPlatform = (device: Pick<Navigator, 'userAgent' | 'platform' | 'maxTouchPoints'>): InstallPlatform => {
  if (/iPad|iPhone|iPod/i.test(device.userAgent)
    || (device.platform === 'MacIntel' && device.maxTouchPoints > 1)) return 'ios';
  if (/Android/i.test(device.userAgent)) return 'android';
  return null;
};

export function createPwaInstallController(browser: InstallBrowser) {
  const platform = getInstallPlatform(browser.navigator);
  const displayMode = browser.matchMedia('(display-mode: standalone)');
  const isStandalone = () => displayMode.matches
    || (browser.navigator as Navigator & { standalone?: boolean }).standalone === true;
  let dismissed = dismissedBrowsers.has(browser);
  try {
    dismissed ||= browser.sessionStorage.getItem(dismissalKey) === '1';
  } catch {
    // Installation guidance must still work when storage is unavailable.
  }
  let deferredPrompt: InstallPromptEvent | null = null;
  let state: PwaInstallState = {
    platform,
    visible: platform !== null && !isStandalone() && !dismissed,
    canPrompt: false,
    installing: false,
    error: null,
  };
  const listeners = new Set<(state: PwaInstallState) => void>();
  const update = (next: Partial<PwaInstallState>) => {
    state = { ...state, ...next };
    listeners.forEach((listener) => listener(state));
  };
  const dismiss = () => {
    dismissed = true;
    dismissedBrowsers.add(browser);
    try {
      browser.sessionStorage.setItem(dismissalKey, '1');
    } catch {
      // The in-memory dismissal above lasts until this page session ends.
    }
    deferredPrompt = null;
    update({ visible: false, canPrompt: false, installing: false, error: null });
  };
  const beforeInstall = (event: Event) => {
    if (platform !== 'android' || dismissed || isStandalone()) return;
    const promptEvent = event as InstallPromptEvent;
    if (typeof promptEvent.prompt !== 'function') return;
    event.preventDefault();
    deferredPrompt = promptEvent;
    update({ visible: true, canPrompt: !state.installing, error: null });
  };
  const displayModeChanged = () => {
    if (isStandalone()) {
      deferredPrompt = null;
      update({ visible: false, canPrompt: false, installing: false, error: null });
    } else if (!dismissed) {
      update({ visible: platform !== null });
    }
  };
  const install = async () => {
    if (!deferredPrompt || !state.visible || state.installing) return;
    const promptEvent = deferredPrompt;
    // Browsers allow only one prompt() call per event, including failed calls.
    deferredPrompt = null;
    update({ canPrompt: false, installing: true, error: null });
    try {
      // Call synchronously from the button handler to retain user activation.
      const result = await promptEvent.prompt();
      const choice = result ?? await promptEvent.userChoice;
      if (!choice) throw new Error('Installation choice unavailable');
      // Acceptance and dismissal both suppress further prompting this session.
      // This is not a claim that installation can be detected in every browser.
      dismiss();
    } catch {
      update({
        installing: false,
        canPrompt: deferredPrompt !== null && !dismissed && !isStandalone(),
        error: state.visible ? 'The install prompt could not open. Try your browser’s menu instead.' : null,
      });
    }
  };
  const subscribe = (listener: (state: PwaInstallState) => void) => {
    listeners.add(listener);
    if (listeners.size === 1 && platform !== null) {
      browser.addEventListener('beforeinstallprompt', beforeInstall);
      browser.addEventListener('appinstalled', dismiss);
      if (displayMode.addEventListener) displayMode.addEventListener('change', displayModeChanged);
      else displayMode.addListener(displayModeChanged);
      displayModeChanged();
    }
    listener(state);
    return () => {
      listeners.delete(listener);
      if (listeners.size === 0) {
        browser.removeEventListener('beforeinstallprompt', beforeInstall);
        browser.removeEventListener('appinstalled', dismiss);
        if (displayMode.removeEventListener) displayMode.removeEventListener('change', displayModeChanged);
        else displayMode.removeListener(displayModeChanged);
      }
    };
  };
  return { getState: () => state, subscribe, install, dismiss };
}
